import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Download, Lock, AlertTriangle, CheckCircle2, FileText, Car, Building2, Layers, Eye, Info } from 'lucide-react';

function fmt(v) { return v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v) : '—'; }

function monthOptions() {
    const opts = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        opts.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
}

export default function TvaPage() {
    const [mois, setMois] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [creditInput, setCreditInput] = useState('');
    const [savingCredit, setSavingCredit] = useState(false);
    const [cloturing, setCloturing] = useState(false);
    const [agencies, setAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState('');
    const months = useMemo(monthOptions, []);

    useEffect(() => {
        axios.get(`${API_URL}/api/agencies`, { withCredentials: true }).then(r => {
            const list = r.data || [];
            setAgencies(list);
            if (list.length === 1) setSelectedAgency(list[0].id);
        }).catch(() => {});
    }, []);

    const load = useCallback(async (m) => {
        setLoading(true);
        try {
            const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
            const r = await axios.get(`${API_URL}/api/tva/${m}${params}`, { withCredentials: true });
            setData(r.data);
            setCreditInput(r.data.solde?.credit_anterieur || '');
        } catch (e) {
            if (e?.response?.data?.detail?.includes('Agence requise') && agencies.length > 0) {
                setData(null);
            } else {
                toast.error(e?.response?.data?.detail || 'Erreur chargement TVA');
            }
        }
        setLoading(false);
    }, [selectedAgency, agencies.length]);

    useEffect(() => { if (selectedAgency) load(mois); }, [mois, load, selectedAgency]);

    const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

    const saveCredit = async () => {
        setSavingCredit(true);
        try {
            await axios.put(`${API_URL}/api/tva/${mois}/credit`, { montant: parseFloat(creditInput) || 0, agency_id: selectedAgency }, { withCredentials: true });
            toast.success('Crédit TVA mis à jour');
            load(mois);
        } catch (e) { toast.error('Erreur sauvegarde crédit'); }
        setSavingCredit(false);
    };

    const cloturer = async () => {
        if (!confirm('Clôturer cette période TVA ? Un snapshot des données sera conservé.')) return;
        setCloturing(true);
        try {
            await axios.post(`${API_URL}/api/tva/${mois}/cloturer`, { agency_id: selectedAgency }, { withCredentials: true });
            toast.success('Période clôturée');
            load(mois);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur clôture'); }
        setCloturing(false);
    };

    const exportXls = () => {
        const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
        window.open(`${API_URL}/api/tva/${mois}/export${params}`, '_blank');
    };

    const isCloture = data?.statut === 'cloture';
    const s = data?.solde;

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="tva-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900" data-testid="tva-title">Préparation TVA</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Contrôle et synthèse mensuelle pour votre expert-comptable</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {agencies.length > 1 && (
                        <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                            <SelectTrigger className="w-48 h-9 text-sm" data-testid="tva-agency-select"><SelectValue placeholder="Sélectionner une agence" /></SelectTrigger>
                            <SelectContent>{agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name || `Agence ${a.id.slice(-4)}`}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                    <Select value={mois} onValueChange={setMois}>
                        <SelectTrigger className="w-48 h-9 text-sm" data-testid="tva-month-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {isCloture && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"><Lock className="w-3 h-3" />Clôturé</span>}
                    {!isCloture && data && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">Brouillon</span>}
                </div>
            </div>

            {loading && <div className="text-center py-12 text-gray-400">Chargement...</div>}

            {!data && !loading && agencies.length > 1 && !selectedAgency && (
                <div className="text-center py-16 text-gray-400">
                    <Building2 className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Sélectionnez une agence pour afficher la préparation TVA</p>
                </div>
            )}

            {data && !loading && (
                <>
                    {/* Anomalies banner */}
                    {data.anomalies_count > 0 && (
                        <button onClick={() => toggle('anomalies')} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-left" data-testid="tva-anomalies-banner">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                <span className="text-sm font-medium text-amber-800">{data.anomalies_count} opération(s) à vérifier avant clôture</span>
                            </div>
                            {expanded.anomalies ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                        </button>
                    )}
                    {expanded.anomalies && data.anomalies.length > 0 && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden">
                            <div className="divide-y divide-amber-100">
                                {data.anomalies.map((a, i) => (
                                    <div key={i} className="px-4 py-2.5 text-[12px] flex items-start gap-2">
                                        {a.severity === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /> : <Info className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />}
                                        <div>
                                            <span className="font-medium text-gray-700">{a.nom || a.fournisseur || a.libelle || a.id || ''}</span>
                                            {a.immatriculation && <span className="ml-1 text-gray-500">({a.immatriculation})</span>}
                                            <span className="ml-1 text-gray-500">— {a.anomalie}</span>
                                            {a.tva_potentielle > 0 && <span className="ml-1 text-amber-700 font-medium">(TVA potentielle : {fmt(a.tva_potentielle)})</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TVA COLLECTÉE */}
                    <Section title="TVA collectée" total={data.collectee.total} icon={<Car className="w-4 h-4" />}
                        badge={`${data.collectee.marge.count + data.collectee.classique.count} vente(s)`}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <MiniCard label="TVA sur marge" value={data.collectee.marge.total} count={data.collectee.marge.count} />
                            <MiniCard label="TVA classique" value={data.collectee.classique.total} count={data.collectee.classique.count} />
                            <MiniCard label="Exonéré" value={0} count={data.collectee.exonere?.count || 0} />
                        </div>
                        {data.collectee.a_verifier.count > 0 && (
                            <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3 border border-amber-200">
                                {data.collectee.a_verifier.count} véhicule(s) avec régime TVA à vérifier — exclus du total
                            </div>
                        )}
                        <DetailToggle id="coll" expanded={expanded} toggle={toggle} label="Détail des ventes">
                            <table className="w-full text-[11px]">
                                <thead><tr className="text-gray-400 border-b">
                                    <th className="text-left py-1.5 font-medium">Véhicule</th>
                                    <th className="text-left py-1.5 font-medium">Régime</th>
                                    <th className="text-right py-1.5 font-medium">Vente TTC</th>
                                    <th className="text-right py-1.5 font-medium">Base taxable</th>
                                    <th className="text-right py-1.5 font-medium">TVA</th>
                                </tr></thead>
                                <tbody>
                                    {[...data.collectee.marge.detail, ...data.collectee.classique.detail].map((e, i) => (
                                        <tr key={i} className="border-b border-gray-50">
                                            <td className="py-1.5"><span className="font-medium">{e.nom}</span> <span className="text-gray-400 font-mono text-[10px]">{e.immatriculation}</span></td>
                                            <td className="py-1.5 text-gray-500">{e.regime_label}</td>
                                            <td className="py-1.5 text-right font-mono">{fmt(e.vente_reelle_ttc)}</td>
                                            <td className="py-1.5 text-right font-mono">{fmt(e.base_taxable)}</td>
                                            <td className="py-1.5 text-right font-mono font-medium">{fmt(e.tva_collectee)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </DetailToggle>
                    </Section>

                    {/* TVA DÉDUCTIBLE */}
                    <Section title="TVA déductible" total={data.deductible.total} icon={<FileText className="w-4 h-4" />}
                        badge={`${data.deductible.frais_vehicules.count + data.deductible.frais_mutualises.count + data.deductible.charges.count} facture(s)`}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <MiniCard label="Frais véhicules" value={data.deductible.frais_vehicules.total} count={data.deductible.frais_vehicules.count} />
                            <MiniCard label="Frais mutualisés" value={data.deductible.frais_mutualises.total} count={data.deductible.frais_mutualises.count} warn={data.deductible.frais_mutualises.a_verifier?.length} />
                            <MiniCard label="Charges" value={data.deductible.charges.total} count={data.deductible.charges.count} warn={data.deductible.charges.a_verifier?.length} />
                        </div>
                        <div className="text-[11px] text-gray-400 bg-gray-50 rounded-lg px-3 py-2 mb-3 border border-gray-100">
                            Frais de bureau exclus — données TVA non disponibles
                        </div>
                        <DetailToggle id="ded" expanded={expanded} toggle={toggle} label="Détail des factures">
                            <table className="w-full text-[11px]">
                                <thead><tr className="text-gray-400 border-b">
                                    <th className="text-left py-1.5 font-medium">Source</th>
                                    <th className="text-left py-1.5 font-medium">Fournisseur</th>
                                    <th className="text-left py-1.5 font-medium">N° facture</th>
                                    <th className="text-left py-1.5 font-medium">Date</th>
                                    <th className="text-right py-1.5 font-medium">TTC</th>
                                    <th className="text-right py-1.5 font-medium">Taux</th>
                                    <th className="text-right py-1.5 font-medium">TVA</th>
                                    <th className="text-center py-1.5 font-medium">Just.</th>
                                </tr></thead>
                                <tbody>
                                    {[
                                        ...data.deductible.frais_vehicules.detail.map(e => ({ ...e, _src: 'Véhicule' })),
                                        ...data.deductible.frais_mutualises.detail.map(e => ({ ...e, _src: 'Mutualisé' })),
                                        ...data.deductible.charges.detail.map(e => ({ ...e, _src: 'Charge' })),
                                    ].map((e, i) => (
                                        <tr key={i} className="border-b border-gray-50">
                                            <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${e._src === 'Véhicule' ? 'bg-blue-50 text-blue-700' : e._src === 'Mutualisé' ? 'bg-indigo-50 text-indigo-700' : 'bg-orange-50 text-orange-700'}`}>{e._src}</span></td>
                                            <td className="py-1.5 text-gray-600">{e.fournisseur || e.libelle || '—'}</td>
                                            <td className="py-1.5 text-gray-500 font-mono">{e.numero_facture || '—'}</td>
                                            <td className="py-1.5 text-gray-500">{e.date_facture || '—'}</td>
                                            <td className="py-1.5 text-right font-mono">{fmt(e.montant_ttc)}</td>
                                            <td className="py-1.5 text-right text-gray-500">{e.taux_tva}%</td>
                                            <td className="py-1.5 text-right font-mono font-medium">{fmt(e.tva_deductible)}</td>
                                            <td className="py-1.5 text-center">{e.scan_document_path ? <FileText className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </DetailToggle>
                    </Section>

                    {/* SOLDE */}
                    {s && (
                        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="tva-solde">
                            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                                <h3 className="text-sm font-semibold text-gray-800">Solde TVA</h3>
                            </div>
                            <div className="px-5 py-4 space-y-2 text-[13px]">
                                <Row label="TVA collectée" value={s.tva_collectee} />
                                <Row label="TVA déductible" value={-s.tva_deductible} negative />
                                <div className="border-t border-gray-100 my-2" />
                                <Row label="TVA nette avant report" value={s.tva_nette} bold />
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="text-gray-500 text-[12px]">Crédit antérieur :</span>
                                    <Input type="number" value={creditInput} onChange={e => setCreditInput(e.target.value)}
                                        className="w-28 h-8 text-[12px]" disabled={isCloture} data-testid="tva-credit-input" />
                                    {!isCloture && <Button size="sm" variant="outline" onClick={saveCredit} disabled={savingCredit} className="h-8 text-[11px]">Enregistrer</Button>}
                                </div>
                                {s.credit_utilise > 0 && <Row label="Crédit utilisé" value={-s.credit_utilise} negative />}
                                {s.credit_restant > 0 && <div className="text-[11px] text-gray-400">Crédit restant : {fmt(s.credit_restant)}</div>}
                                <div className="border-t border-gray-200 my-3" />
                                {s.tva_a_payer > 0 ? (
                                    <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-red-50 border border-red-200">
                                        <span className="font-semibold text-red-800 text-sm">TVA estimée à payer</span>
                                        <span className="font-bold text-red-700 text-lg font-mono" data-testid="tva-a-payer">{fmt(s.tva_a_payer)}</span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                        <span className="font-semibold text-emerald-800 text-sm">Crédit de TVA</span>
                                        <span className="font-bold text-emerald-700 text-lg font-mono" data-testid="tva-credit">{fmt(s.nouveau_credit)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3">
                        <Button onClick={exportXls} variant="outline" className="h-10 px-5 text-sm" data-testid="tva-export-btn">
                            <Download className="w-4 h-4 mr-2" />Exporter pour le comptable
                        </Button>
                        {!isCloture && (
                            <Button onClick={cloturer} disabled={cloturing} className="h-10 px-5 text-sm bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="tva-cloturer-btn">
                                <Lock className="w-4 h-4 mr-2" />{cloturing ? 'Clôture...' : 'Clôturer la période'}
                            </Button>
                        )}
                    </div>

                    {/* Footer disclaimer */}
                    <p className="text-[11px] text-gray-400 leading-relaxed">
                        Ce document est un outil de préparation et de contrôle. Il ne remplace pas la validation et la déclaration effectuées par votre expert-comptable.
                        Les montants « à vérifier » sont exclus des totaux. Toute anomalie doit être résolue avant clôture.
                    </p>
                </>
            )}
        </div>
    );
}

function Section({ title, total, icon, badge, children }) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-gray-500">{icon}</span>
                    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                    {badge && <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{badge}</span>}
                </div>
                <span className="text-base font-bold font-mono text-gray-900">{fmt(total)}</span>
            </div>
            <div className="px-5 py-4">{children}</div>
        </div>
    );
}

function MiniCard({ label, value, count, warn }) {
    return (
        <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-1">{label}</div>
            <div className="text-sm font-bold font-mono text-gray-900">{fmt(value)}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">
                {count} opération(s)
                {warn > 0 && <span className="ml-1 text-amber-600">+ {warn} à vérifier</span>}
            </div>
        </div>
    );
}

function DetailToggle({ id, expanded, toggle, label, children }) {
    return (
        <div>
            <button onClick={() => toggle(id)} className="flex items-center gap-1.5 text-[11px] text-[#6366F1] hover:text-[#4F46E5] font-medium mb-2">
                <Eye className="w-3.5 h-3.5" />{label}
                {expanded[id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {expanded[id] && <div className="overflow-x-auto">{children}</div>}
        </div>
    );
}

function Row({ label, value, negative, bold }) {
    return (
        <div className="flex justify-between">
            <span className={`text-gray-500 ${bold ? 'font-medium text-gray-700' : ''}`}>{label}</span>
            <span className={`font-mono ${negative ? 'text-red-600' : ''} ${bold ? 'font-semibold' : ''}`}>{fmt(value)}</span>
        </div>
    );
}
